/* eslint-disable max-lines -- registration and effects stay together so they share the Solid onMount owner */
import { Accessor, createEffect, createMemo, on, Setter } from 'solid-js';
import { ImagingApiService } from '@data-access/geospatial/imaging';
import { Web3DViewer } from '@technology/web3d';
import { IDataLoadingService } from '@trimble/data-loading';
import { asError, type ILogger } from '@trimble/logging';
import { PaneSyncer } from '@trimble/pane-sync';
import {
  create3DViewer,
  Point2D,
  ThreeJsManager,
  Web3DViewerWrapper,
} from '@trimble/utilities';
import {
  CapabilitiesCollection,
  CapabilityRegistry,
  CapabilityRegistryContainer,
  Hide2DMarkupCapabilityCoordinator,
  MutuallyExclusiveCapabilityCoordinator,
  NavigationCapabilityCoordinator,
} from '@trimble/web3d';
import {
  FeatureToggleCapabilityCoordinator,
  FetchGeoDataFiles,
  MapTileConfig,
  PaneVisibilityCoordinator,
  resolveSyncCoordinator,
  TileMapCoordinatesCapabilityCoordinator,
  TileMapLoadLimitBounds,
  ViewMode,
  ViewModeCapabilityCoordinator,
} from '@mobile-mapping/web3d';
import {
  AlignmentNavigationCapability,
  FreeViewCapability,
  ImageCapabilityBase,
  SelectionActionsCapability,
  StationsNavigationCapability,
  Web3dSettings,
} from '@trimble/web3d-capabilities';
import {
  PointCloudCapabilityProvider,
  PointCloudStylingCapabilityProvider,
  StationsCapabilityProvider,
  TrajectoryMetadataProvider,
  TrimBimCapabilityProvider,
  ViewsCapabilityProvider,
} from '@trimble/web3d-providers';

import { CadEditingCapability } from './feature-extraction/CadEditingCapability';
import type { FeatureExtractionController } from './feature-extraction/feature-extraction-controller';
import { useKeyboardStationNavigation } from './hooks/useKeyboardStationNavigation';
import {
  addTilesetPrefetchEffect,
  type TilesetPrefetchContext,
} from './station-load/tileset-prefetch';

export interface PaneViewerViewModeDeps {
  readonly initial: ViewMode;
  readonly dataLoadingService: IDataLoadingService;
  readonly web3dSettings: Accessor<Web3dSettings>;
  readonly fetchGeoDataFiles: FetchGeoDataFiles;
  readonly datasetCsib: Accessor<string | undefined>;
  readonly datasetCenter: Accessor<Point2D | undefined>;
  readonly datasetBounds: Accessor<TileMapLoadLimitBounds | undefined>;
  readonly mapTileConfig: MapTileConfig;
}

export interface PaneViewerSessionRuntime {
  readonly isInStationNavigation: Accessor<boolean>;
  readonly viewModeType: Accessor<ViewMode['type'] | undefined>;
  readonly isHovered: Accessor<boolean>;
  readonly keyDownEvent: Accessor<KeyboardEvent | null>;
  readonly imagingApi: ImagingApiService;
  readonly tilesetPrefetchContext: Accessor<TilesetPrefetchContext>;
}

export interface PaneViewerSessionParams {
  readonly placeholder: HTMLDivElement;
  readonly web3DPath: string;
  readonly capRegContainer: CapabilityRegistryContainer;
  readonly setViewer: Setter<Web3DViewer | undefined>;
  readonly isAlignmentMeasurementInteractionAllowed: Accessor<boolean>;
  readonly isDrawMarkupCreationAllowed: Accessor<boolean>;
  readonly logger: ILogger;
  readonly cadEditingSession: Accessor<FeatureExtractionController | undefined>;
  readonly allowMarkups: Accessor<boolean>;
  readonly allowMarkupEditing: Accessor<boolean>;
  readonly capabilityRegistry: CapabilityRegistry;
  readonly viewMode: PaneViewerViewModeDeps;
  readonly runtime: PaneViewerSessionRuntime;
  registerSyncedPane?(paneSyncer: PaneSyncer): () => void;
}

export interface PaneViewerSession {
  readonly viewerWrapper: Web3DViewerWrapper;
  readonly viewModeCoordinator: ViewModeCapabilityCoordinator;
  cleanup(): void;
}

interface RegisterPaneCapabilitiesDeps {
  readonly logger: ILogger;
  readonly cadEditingSession: Accessor<FeatureExtractionController | undefined>;
  readonly allowMarkups: Accessor<boolean>;
  readonly allowMarkupEditing: Accessor<boolean>;
  readonly capabilityRegistry: CapabilityRegistry;
  readonly viewMode: PaneViewerViewModeDeps;
}

export function createPaneViewerSession(
  params: PaneViewerSessionParams
): PaneViewerSession {
  const {
    placeholder,
    web3DPath,
    capRegContainer,
    setViewer,
    isAlignmentMeasurementInteractionAllowed,
    isDrawMarkupCreationAllowed,
  } = params;

  const viewerWrapper = create3DViewer(placeholder, web3DPath, {
    fxaa: undefined,
    msaa: false,
    renderEdges: true,
    ssao: true,
    orderIndependentTransparency: true,
    vertexInterpolationMaterials: false,
    transparencyPeelsCount: 1,
  });
  viewerWrapper.isAlignmentMeasurementInteractionAllowed =
    isAlignmentMeasurementInteractionAllowed;
  viewerWrapper.isDrawMarkupCreationAllowed = isDrawMarkupCreationAllowed;
  setViewer(viewerWrapper.viewer);

  capRegContainer.setViewerWrapper(viewerWrapper);

  const threeJsManager = new ThreeJsManager(
    viewerWrapper.viewer,
    viewerWrapper.threejsPlugin
  );

  const { navCapCoord, viewModeCoordinator } = registerPaneCapabilities(
    viewerWrapper,
    capRegContainer,
    threeJsManager,
    {
      logger: params.logger,
      cadEditingSession: params.cadEditingSession,
      allowMarkups: params.allowMarkups,
      allowMarkupEditing: params.allowMarkupEditing,
      capabilityRegistry: params.capabilityRegistry,
      viewMode: params.viewMode,
    }
  );

  const paneSyncer = new PaneSyncer(
    viewerWrapper.viewer,
    capRegContainer,
    resolveSyncCoordinator,
    navCapCoord
  );
  const unregisterPaneSyncer = params.registerSyncedPane?.(paneSyncer);

  addPaneViewerEffects(
    viewerWrapper,
    capRegContainer,
    paneSyncer,
    viewModeCoordinator,
    params.capabilityRegistry,
    params.runtime
  );

  return {
    viewerWrapper,
    viewModeCoordinator,
    cleanup(): void {
      params.capabilityRegistry.removePaneContainer(capRegContainer);
      unregisterPaneSyncer?.();

      void (async () => {
        try {
          await viewModeCoordinator.cleanup();
          await capRegContainer.unregister(
            TileMapCoordinatesCapabilityCoordinator
          );
        } catch (error: unknown) {
          params.logger.error(
            'ViewModeCapabilityCoordinator cleanup failed',
            undefined,
            asError(error)
          );
        } finally {
          params.capabilityRegistry.unregisterPaneContainer(viewerWrapper);
        }
      })();
    },
  };
}

function registerPaneCapabilities(
  viewerWrapper: Web3DViewerWrapper,
  capRegContainer: CapabilityRegistryContainer,
  threeJsManager: ThreeJsManager,
  deps: RegisterPaneCapabilitiesDeps
): {
  navCapCoord: NavigationCapabilityCoordinator;
  viewModeCoordinator: ViewModeCapabilityCoordinator;
} {
  const {
    logger,
    cadEditingSession,
    allowMarkups,
    allowMarkupEditing,
    capabilityRegistry,
    viewMode,
  } = deps;

  const freeViewCap = capRegContainer.register(
    FreeViewCapability,
    new FreeViewCapability(viewerWrapper.viewer)
  );

  const trajectoryMetadataProvider = new TrajectoryMetadataProvider(
    viewerWrapper.viewer
  );

  capRegContainer.register(
    SelectionActionsCapability,
    new SelectionActionsCapability(viewerWrapper.viewer, (event) => {
      trajectoryMetadataProvider
        .setMaterialColors(event.modelId, [...event.entityIds], event.color)
        .catch((error: unknown) => {
          logger.error(
            `Failed to apply trajectory color override for model ${event.modelId}`,
            undefined,
            asError(error)
          );
        });
    })
  );

  capRegContainer.register(
    CapabilitiesCollection<ImageCapabilityBase>,
    new CapabilitiesCollection<ImageCapabilityBase>()
  );

  const pcStylingProvider = capRegContainer.get(
    PointCloudStylingCapabilityProvider
  );

  capRegContainer.register(
    PointCloudCapabilityProvider,
    new PointCloudCapabilityProvider(viewerWrapper.viewer, pcStylingProvider())
  );

  capRegContainer.register(
    TrimBimCapabilityProvider,
    new TrimBimCapabilityProvider(viewerWrapper.viewer)
  );
  capRegContainer.register(
    TrajectoryMetadataProvider,
    trajectoryMetadataProvider
  );

  capRegContainer.register(
    CadEditingCapability,
    new CadEditingCapability(viewerWrapper.viewer, cadEditingSession)
  );

  // Highlight is always on; the color is user-configurable via preferences.
  viewerWrapper.viewer.settings.clippingHighlight = true;

  capRegContainer.register(
    MutuallyExclusiveCapabilityCoordinator,
    new MutuallyExclusiveCapabilityCoordinator(capRegContainer)
  );

  const navCapCoord = capRegContainer.register(
    NavigationCapabilityCoordinator,
    new NavigationCapabilityCoordinator(capRegContainer, freeViewCap)
  );

  capRegContainer.register(
    Hide2DMarkupCapabilityCoordinator,
    new Hide2DMarkupCapabilityCoordinator(
      capRegContainer,
      viewerWrapper,
      allowMarkups,
      allowMarkupEditing
    )
  );

  const viewModeCoordinator = capRegContainer.register(
    ViewModeCapabilityCoordinator,
    new ViewModeCapabilityCoordinator(
      capRegContainer,
      viewMode.initial,
      {
        threeJsManager,
        dataLoadingService: viewMode.dataLoadingService,
        web3dSettings: viewMode.web3dSettings,
        datasetCsib: viewMode.datasetCsib,
        datasetCenter: viewMode.datasetCenter,
        getViewsProvider: () =>
          capabilityRegistry.get(ViewsCapabilityProvider)(),
      },
      viewerWrapper
    )
  );

  capRegContainer.register(
    TileMapCoordinatesCapabilityCoordinator,
    new TileMapCoordinatesCapabilityCoordinator(
      capRegContainer,
      viewerWrapper.viewer,
      viewMode.fetchGeoDataFiles,
      viewMode.mapTileConfig,
      viewMode.datasetCsib,
      viewMode.datasetCenter,
      viewModeCoordinator.viewMode,
      viewMode.datasetBounds
    )
  );

  capRegContainer.register(
    FeatureToggleCapabilityCoordinator,
    new FeatureToggleCapabilityCoordinator(capRegContainer)
  );

  capRegContainer.register(
    PaneVisibilityCoordinator,
    new PaneVisibilityCoordinator(
      () => capabilityRegistry.get(ViewsCapabilityProvider)(),
      () => viewMode.web3dSettings().imageViewPointcloudClippingDistance
    )
  );

  return { navCapCoord, viewModeCoordinator };
}

function addPaneViewerEffects(
  viewerWrapper: Web3DViewerWrapper,
  capRegContainer: CapabilityRegistryContainer,
  paneSyncer: PaneSyncer,
  viewModeCoordinator: ViewModeCapabilityCoordinator,
  capabilityRegistry: CapabilityRegistry,
  runtime: PaneViewerSessionRuntime
): void {
  createEffect(
    on(viewModeCoordinator.contentUpdated, () => {
      paneSyncer.reset();
    })
  );

  createEffect(
    on(
      () =>
        capRegContainer
          .get(TileMapCoordinatesCapabilityCoordinator)()
          ?.contentUpdated(),
      () => {
        paneSyncer.reset();
      }
    )
  );

  createEffect(() => {
    const vm = viewModeCoordinator.viewMode();
    const inStationNavigation = runtime.isInStationNavigation();
    const isAlignmentNavActive =
      capRegContainer.get(AlignmentNavigationCapability)()?.isActive() ?? false;

    viewerWrapper.compassPlugin.enabled =
      vm.type === '3D' && !inStationNavigation;

    viewerWrapper.compassPlugin.interactive = !(
      isAlignmentNavActive || inStationNavigation
    );
  });

  createEffect(
    on(
      [
        () => viewModeCoordinator.currentCameraIndex() ?? null,
        () =>
          capRegContainer.get(CapabilitiesCollection<ImageCapabilityBase>)(),
      ],
      ([camInd, imgCaps]) => {
        if (imgCaps == null || camInd == null) {
          return;
        }
        imgCaps.capabilities[camInd]?.activate();
      }
    )
  );

  createEffect(
    on(
      () =>
        capRegContainer.get(CapabilitiesCollection<ImageCapabilityBase>)()
          ?.capabilities ?? [],
      (imgCaps, prevImageCaps) => {
        prevImageCaps
          ?.filter((p) => !imgCaps.some((c) => c == p))
          .forEach((p) => void p.cleanup());
      }
    )
  );

  const activeCameraName = createMemo(() => {
    const activeCamIndex = viewModeCoordinator.currentCameraIndex() ?? 0;
    const imageCaps = capRegContainer.get(
      CapabilitiesCollection<ImageCapabilityBase>
    )();
    return imageCaps?.capabilities[activeCamIndex]?.cameraName;
  });

  addTilesetPrefetchEffect({
    stationsProvider: capabilityRegistry.get(StationsCapabilityProvider),
    imagingApi: runtime.imagingApi,
    viewModeCapCoord: () => viewModeCoordinator,
    tilesetPrefetchContext: runtime.tilesetPrefetchContext,
    activeCameraName,
  });

  useKeyboardStationNavigation({
    stationNavigationController: () =>
      viewModeCoordinator.stationNavigationController(),
    viewMode: runtime.viewModeType,
    getStationsNavigationCapability: () =>
      capRegContainer.get(StationsNavigationCapability),
    isHovered: runtime.isHovered,
    keyDownEvent: runtime.keyDownEvent,
  });
}
